import { useEffect, useMemo, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, Mail, Sparkles, Calendar } from "lucide-react";
import { api } from "../../lib/api";

const RANGE_OPTIONS = [
  { id: "current_month", label: "This Month" },
  { id: "last_month", label: "Last Month" },
  { id: "custom", label: "Custom Range" },
];

function formatDay(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <div className="kb-glass rounded-2xl p-6">
      <div className="h-10 w-10 rounded-xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center mb-4">
        <Icon size={18} className="text-[#d4a840]" />
      </div>
      <div className="font-display text-3xl font-bold text-white">{value}</div>
      <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{label}</div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="kb-glass rounded-xl px-3 py-2 bg-[#0a1330] text-sm">
      <div className="text-white/60 text-xs">{label}</div>
      <div className="text-white font-semibold">{payload[0].value} enquiries</div>
    </div>
  );
};

export function AnalyticsTab() {
  const [range, setRange] = useState("current_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const query = useMemo(() => {
    if (range === "custom") {
      if (!customFrom || !customTo) return null;
      return { range: "custom", from: customFrom, to: customTo };
    }
    return { range };
  }, [range, customFrom, customTo]);

  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    setLoading(true);
    api
      .get("/admin/leads/stats", { params: query })
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const chartData = (stats?.series || []).map((s) => ({ label: formatDay(s.date), count: s.count }));
  const onboardingCount = stats?.by_source?.onboarding_dialog || 0;
  const contactCount = stats?.by_source?.contact_form || 0;

  return (
    <div data-testid="admin-analytics-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="kb-label mb-3">Overview</div>
          <h1 className="font-display text-3xl font-bold text-white">Enquiry Analytics</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setRange(opt.id)}
              data-testid={`range-${opt.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                range === opt.id
                  ? "bg-[#d4a840] text-[#050A15] border-[#d4a840]"
                  : "border-[#d4a840]/30 text-white/70 hover:text-white hover:border-[#d4a840]/60"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {range === "custom" ? (
        <div className="flex flex-wrap items-center gap-3 mb-8 kb-glass rounded-xl px-5 py-4">
          <Calendar size={16} className="text-[#d4a840]" />
          <input
            type="date"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            data-testid="range-custom-from"
            className="bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#d4a840] focus:outline-none"
          />
          <span className="text-white/40 text-sm">to</span>
          <input
            type="date"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            data-testid="range-custom-to"
            className="bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-lg px-3 py-2 text-sm text-white focus:border-[#d4a840] focus:outline-none"
          />
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <StatTile icon={TrendingUp} label="Total Enquiries" value={loading ? "…" : stats?.total ?? 0} />
        <StatTile icon={Mail} label="From Contact Form" value={loading ? "…" : contactCount} />
        <StatTile icon={Sparkles} label="From AI Onboarding" value={loading ? "…" : onboardingCount} />
      </div>

      <div className="kb-glass rounded-2xl p-6" data-testid="admin-analytics-chart">
        <div className="text-sm text-white/60 mb-4">Enquiries per day</div>
        <div className="h-72">
          {chartData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 12, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="kbEnquiryFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d4a840" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#d4a840" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.4)" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="count" stroke="#d4a840" strokeWidth={2} fill="url(#kbEnquiryFill)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">
              {loading ? "Loading..." : "No enquiries in this range yet."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
